import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "./api.ts";

/**
 * Caricamento dei dati.
 *
 * Un hook di poche righe al posto di una libreria di data fetching: le
 * schermate di Portman leggono ciascuna una manciata di risorse, e il costo di
 * una dipendenza in più non sarebbe ripagato.
 */

export interface Query<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  /** Il percorso da cui arrivano i `data` mostrati: durante un caricamento non è ancora `path`. */
  percorso: string | null;
  /** Ricarica i dati; utile dopo una modifica. */
  reload: () => void;
}

export function useQuery<T>(path: string | null, deps: unknown[] = []): Query<T> {
  const [data, setData] = useState<T | null>(null);
  const [percorso, setPercorso] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);

  // Evita di aggiornare lo stato dopo che il componente è stato smontato o che
  // una richiesta più recente ha già risposto.
  const latest = useRef(0);

  useEffect(() => {
    if (path === null) {
      setLoading(false);
      return;
    }

    const request = ++latest.current;
    setLoading(true);

    api
      .get<T>(path)
      .then((result) => {
        if (request !== latest.current) return;
        setData(result);
        setPercorso(path);
        setError(null);
      })
      .catch((cause) => {
        if (request !== latest.current) return;
        setError(cause);
      })
      .finally(() => {
        if (request === latest.current) setLoading(false);
      });
  }, [path, nonce, ...deps]);

  return { data, error, loading, percorso, reload: useCallback(() => setNonce((n) => n + 1), []) };
}

/**
 * Azione asincrona con stato di avanzamento ed errore.
 *
 * Racchiude lo schema che si ripeterebbe in ogni pulsante: disattiva mentre
 * lavora, mostra l'errore se fallisce, non lascia mai l'interfaccia bloccata.
 */
export function useAction<Args extends unknown[], Result>(
  action: (...args: Args) => Promise<Result>,
) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const run = useCallback(
    async (...args: Args): Promise<Result | undefined> => {
      setRunning(true);
      setError(null);

      try {
        return await action(...args);
      } catch (cause) {
        setError(cause);
        return undefined;
      } finally {
        setRunning(false);
      }
    },
    [action],
  );

  return { run, running, error, clearError: useCallback(() => setError(null), []) };
}

/** L'errore indica una sessione scaduta? */
export const isUnauthorized = (error: unknown): boolean =>
  error instanceof ApiError && error.isUnauthorized;
